<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
    <div class="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col">

      <!-- Header -->
      <div class="flex items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex-shrink-0">
        <div class="flex items-center gap-3 min-w-0">
          <h3 class="text-base font-semibold text-slate-800 dark:text-slate-100 truncate">{{ campaign.title }}</h3>
          <span :class="campaignStatusClass" class="px-2.5 py-1 rounded-full text-xs font-medium flex-shrink-0">
            {{ campaignStatusLabel }}
          </span>
        </div>
        <div class="flex items-center gap-2 flex-shrink-0 ml-4">
          <button
            type="button"
            :disabled="exporting"
            class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            :title="exporting ? 'Gerando planilha...' : 'Baixar XLSX com todos os contatos'"
            @click="exportXlsx"
          >
            <span :class="exporting ? 'i-lucide-loader animate-spin' : 'i-lucide-download'" class="w-3.5 h-3.5"></span>
            {{ exporting ? 'Gerando…' : 'Exportar Excel' }}
          </button>

          <button
            v-if="canTrigger"
            :disabled="!!actionLoading"
            class="px-3 py-1.5 text-xs font-medium bg-woot-500 text-white rounded-lg hover:bg-woot-600 transition-colors disabled:opacity-50"
            @click="triggerCampaign"
          >{{ actionLoading === 'trigger' ? '...' : '▶ Disparar' }}</button>

          <button
            v-if="canPause"
            :disabled="!!actionLoading"
            class="px-3 py-1.5 text-xs font-medium bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors disabled:opacity-50"
            @click="pauseCampaign"
          >{{ actionLoading === 'pause' ? '...' : '⏸ Pausar' }}</button>

          <button
            v-if="campaign.paused && !rules.cancelled"
            :disabled="!!actionLoading"
            class="px-3 py-1.5 text-xs font-medium bg-woot-500 text-white rounded-lg hover:bg-woot-600 transition-colors disabled:opacity-50"
            @click="resumeCampaign"
          >{{ actionLoading === 'resume' ? '...' : '▶ Retomar' }}</button>

          <button
            v-if="!rules.cancelled && campaign.status !== 'completed'"
            :disabled="!!actionLoading"
            class="px-3 py-1.5 text-xs font-medium border border-red-300 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50"
            @click="showCancelModal = true"
          >🚫 Cancelar</button>

          <button
            :disabled="!!actionLoading"
            class="px-3 py-1.5 text-xs font-medium border border-red-200 text-red-500 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50"
            @click="deleteCampaign"
          >🗑 Excluir</button>

          <button class="ml-1 p-1.5 text-slate-400 hover:text-slate-600 text-xl leading-none" @click="$emit('close')">✕</button>
        </div>
      </div>

      <!-- Stats cards -->
      <div class="grid grid-cols-5 divide-x divide-slate-100 dark:divide-slate-800 border-b border-slate-100 dark:border-slate-800 flex-shrink-0">

        <div
          class="flex flex-col items-center py-4 gap-0.5 relative overflow-hidden cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors"
          title="Ver leads enviados"
          @click="goToLeadsTab('')"
        >
          <div class="absolute top-0 left-0 right-0 h-0.5 bg-blue-400"></div>
          <span class="text-2xl font-bold text-slate-800 dark:text-slate-100">{{ sentCount }}</span>
          <span class="text-xs text-slate-400">Enviados</span>
          <span v-if="stats.total > 0" class="text-xs font-semibold text-blue-500 mt-0.5">{{ sentPct }}% do total</span>
        </div>

        <div
          class="flex flex-col items-center py-4 gap-0.5 relative overflow-hidden cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors"
          title="Ver leads recebidos"
          @click="goToLeadsTab('delivered')"
        >
          <div class="absolute top-0 left-0 right-0 h-0.5 bg-indigo-500"></div>
          <span class="text-2xl font-bold text-indigo-600 dark:text-indigo-400">{{ stats.delivered || 0 }}</span>
          <span class="text-xs text-slate-400">Recebidos</span>
          <span v-if="sentCount > 0" class="text-xs font-semibold text-indigo-500 mt-0.5">{{ deliveredPct }}% enviados</span>
          <span v-else class="text-xs text-slate-300 mt-0.5">—</span>
        </div>

        <div
          class="flex flex-col items-center py-4 gap-0.5 relative overflow-hidden cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors"
          title="Ver leads lidos"
          @click="goToLeadsTab('read')"
        >
          <div class="absolute top-0 left-0 right-0 h-0.5 bg-woot-400"></div>
          <span class="text-2xl font-bold text-woot-600 dark:text-woot-400">{{ stats.read || 0 }}</span>
          <span class="text-xs text-slate-400">Lidos</span>
          <span v-if="sentCount > 0" class="text-xs font-semibold text-woot-500 mt-0.5">{{ readPct }}% enviados</span>
          <span v-else class="text-xs text-slate-300 mt-0.5">—</span>
        </div>

        <div
          class="flex flex-col items-center py-4 gap-0.5 relative overflow-hidden cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors"
          title="Ver leads com falha"
          @click="goToLeadsTab('failed')"
        >
          <div class="absolute top-0 left-0 right-0 h-0.5" :class="(stats.failed || 0) > 0 ? 'bg-red-400' : 'bg-slate-200'"></div>
          <span class="text-2xl font-bold" :class="(stats.failed || 0) > 0 ? 'text-red-500' : 'text-slate-400'">{{ stats.failed || 0 }}</span>
          <span class="text-xs text-slate-400">Falhas</span>
          <span v-if="(stats.failed || 0) > 0" class="text-xs font-semibold text-red-400 mt-0.5">{{ failedPct }}% enviados</span>
          <span v-else class="text-xs text-emerald-400 mt-0.5">Nenhuma</span>
        </div>

        <div
          class="flex flex-col items-center py-4 gap-0.5 relative overflow-hidden cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors"
          title="Ver leads que responderam"
          @click="goToLeadsTab('replied')"
        >
          <div class="absolute top-0 left-0 right-0 h-0.5 bg-emerald-500"></div>
          <span class="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{{ stats.replied || 0 }}</span>
          <span class="text-xs text-slate-400">Respondidas</span>
          <span v-if="sentCount > 0" class="text-xs font-semibold text-emerald-500 mt-0.5">{{ repliedPct }}% enviados</span>
          <span v-else class="text-xs text-slate-300 mt-0.5">—</span>
        </div>

      </div>

      <!-- Tabs -->
      <div class="flex border-b border-slate-100 dark:border-slate-800 px-6 flex-shrink-0">
        <button
          v-for="tab in ['Visão Geral', 'Leads', 'Importar']"
          :key="tab"
          :class="[
            'py-3 px-4 text-sm font-medium border-b-2 transition-colors -mb-px',
            activeTab === tab
              ? 'border-woot-500 text-woot-600 dark:text-woot-400'
              : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
          ]"
          @click="switchTab(tab)"
        >
          {{ tab }}
          <span v-if="tab === 'Leads' && stats.total > 0" class="ml-1 text-xs bg-slate-100 dark:bg-slate-700 rounded-full px-1.5 py-0.5">{{ stats.total }}</span>
        </button>
      </div>

      <!-- ===== TAB: VISÃO GERAL ===== -->
      <div v-if="activeTab === 'Visão Geral'" class="flex-1 overflow-y-auto p-6 space-y-6">

        <!-- Progresso: barra simples de envio + funil de entrega -->
        <div>
          <div class="flex items-center justify-between mb-2">
            <h4 class="text-xs font-semibold text-slate-400 uppercase tracking-wider">Progresso</h4>
            <span class="text-xs text-slate-400">{{ processedCount }} de {{ stats.total || 0 }} processadas</span>
          </div>

          <!-- Barra de envio (enviados vs pendentes) -->
          <div class="h-3 flex rounded-full overflow-hidden bg-slate-100 dark:bg-slate-800">
            <div
              class="bg-woot-500 transition-all duration-700"
              :style="{ width: processedPct + '%' }"
            ></div>
          </div>
          <div class="flex justify-between text-xs text-slate-400 mt-1">
            <span>{{ stats.pending || 0 }} pendente{{ (stats.pending || 0) !== 1 ? 's' : '' }}</span>
            <span>{{ processedPct }}% processado</span>
          </div>

          <!-- Funil de entrega: Enviado → Recebido → Lido → Respondido -->
          <div v-if="sentCount > 0" class="mt-4 grid grid-cols-4 gap-2">
            <div class="text-center">
              <div class="text-lg font-bold text-blue-500">{{ sentCount }}</div>
              <div class="text-xs text-slate-400 mt-0.5">📤 Enviados</div>
              <div class="text-xs text-slate-300 mt-0.5">100%</div>
            </div>
            <div class="text-center">
              <div class="text-lg font-bold text-indigo-500">{{ (stats.delivered || 0) + (stats.read || 0) + (stats.replied || 0) }}</div>
              <div class="text-xs text-slate-400 mt-0.5">📥 Recebidos</div>
              <div class="text-xs text-slate-300 mt-0.5">{{ sentCount > 0 ? Math.round(((stats.delivered||0)+(stats.read||0)+(stats.replied||0)) / sentCount * 100) : 0 }}%</div>
            </div>
            <div class="text-center">
              <div class="text-lg font-bold text-woot-500">{{ (stats.read || 0) + (stats.replied || 0) }}</div>
              <div class="text-xs text-slate-400 mt-0.5">👁 Lidos</div>
              <div class="text-xs text-slate-300 mt-0.5">{{ sentCount > 0 ? Math.round(((stats.read||0)+(stats.replied||0)) / sentCount * 100) : 0 }}%</div>
            </div>
            <div class="text-center">
              <div class="text-lg font-bold text-emerald-500">{{ stats.replied || 0 }}</div>
              <div class="text-xs text-slate-400 mt-0.5">💬 Respondidos</div>
              <div class="text-xs text-slate-300 mt-0.5">{{ sentCount > 0 ? Math.round((stats.replied||0) / sentCount * 100) : 0 }}%</div>
            </div>
          </div>

          <!-- Falhas (se houver) -->
          <div v-if="(stats.failed || 0) > 0" class="mt-3 flex items-center gap-2 text-xs text-red-500 bg-red-50 dark:bg-red-900/20 rounded-lg px-3 py-2">
            <span>⚠</span>
            <span>{{ stats.failed }} mensagem{{ stats.failed !== 1 ? 's' : '' }} com falha — verifique a aba Leads</span>
          </div>

          <!-- Aviso sobre rastreamento -->
          <p v-if="sentCount > 0 && (stats.delivered || 0) === 0 && (stats.read || 0) === 0 && (stats.replied || 0) === 0"
            class="mt-3 text-xs text-slate-400 flex items-center gap-1.5 bg-slate-50 dark:bg-slate-800/60 rounded-lg px-3 py-2">
            <span>ℹ</span>
            Recebido e Lido são atualizados conforme o WhatsApp reporta — pode levar alguns minutos.
          </p>
        </div>

        <!-- 2 col: configurações + mensagem -->
        <div class="grid grid-cols-2 gap-6">

          <!-- Configurações -->
          <div>
            <h4 class="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Configurações</h4>
            <div class="bg-slate-50 dark:bg-slate-800/60 rounded-xl divide-y divide-slate-100 dark:divide-slate-700/50 overflow-hidden">

              <div class="flex items-center justify-between px-4 py-3">
                <span class="text-xs text-slate-500 flex items-center gap-2">
                  <span class="text-base">📤</span> Canal
                </span>
                <span class="text-xs font-medium text-slate-700 dark:text-slate-300 text-right leading-5">
                  <template v-if="campaign.inbox_names && campaign.inbox_names.length > 1">
                    <span v-for="n in campaign.inbox_names" :key="n" class="block">{{ n }}</span>
                  </template>
                  <template v-else>{{ campaign.inbox_name || '—' }}</template>
                </span>
              </div>

              <div class="flex items-center justify-between px-4 py-3">
                <span class="text-xs text-slate-500 flex items-center gap-2">
                  <span class="text-base">👥</span> Leads
                </span>
                <span class="text-xs font-medium text-slate-700 dark:text-slate-300">
                  {{ stats.total || 0 }} contatos
                  <span v-if="(stats.pending || 0) > 0" class="text-orange-500 ml-1">({{ stats.pending }} pendentes)</span>
                </span>
              </div>

              <div v-if="rules.triggered_at" class="flex items-center justify-between px-4 py-3">
                <span class="text-xs text-slate-500 flex items-center gap-2">
                  <span class="text-base">🚀</span> Iniciado em
                </span>
                <span class="text-xs font-medium text-slate-700 dark:text-slate-300 text-right">{{ formatDate(rules.triggered_at) }}</span>
              </div>

              <div v-if="durationEstimate" class="flex items-center justify-between px-4 py-3">
                <span class="text-xs text-slate-500 flex items-center gap-2">
                  <span class="text-base">⏳</span> Duração estimada
                </span>
                <span class="text-xs font-medium text-slate-700 dark:text-slate-300">{{ durationEstimate }}</span>
              </div>

              <div v-if="rules.send_interval" class="flex items-center justify-between px-4 py-3">
                <span class="text-xs text-slate-500 flex items-center gap-2">
                  <span class="text-base">⏱</span> Intervalo entre envios
                </span>
                <span class="text-xs font-medium text-slate-700 dark:text-slate-300">{{ formatSeconds(rules.send_interval) }}</span>
              </div>

              <div v-if="rules.batch_size > 0" class="flex items-start justify-between px-4 py-3">
                <span class="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                  <span class="text-base">📦</span> Anti-bloqueio
                </span>
                <span class="text-xs font-medium text-slate-700 dark:text-slate-300 text-right leading-5">
                  Pausa a cada {{ rules.batch_size }} msgs
                  <span v-if="rules.batch_interval > 0" class="block text-slate-400 font-normal">por {{ formatSeconds(rules.batch_interval) }}</span>
                </span>
              </div>

            </div>
          </div>

          <!-- Mensagem preview -->
          <div>
            <h4 class="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Mensagem</h4>
            <div class="relative bg-[#dcf8c6] dark:bg-slate-700 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
              <p class="text-sm text-slate-800 dark:text-slate-100 whitespace-pre-wrap leading-relaxed">{{ campaign.message }}</p>
              <div v-if="rules.image_url" class="mt-2 pt-2 border-t border-woot-200/60 dark:border-slate-600 flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                <span>🖼</span> Envia com imagem
              </div>
            </div>
            <!-- Variaveis detectadas -->
            <div v-if="messageVars.length > 0" class="mt-2 flex flex-wrap gap-1.5">
              <span
                v-for="v in messageVars" :key="v"
                class="text-xs font-mono bg-woot-50 dark:bg-woot-900/30 text-woot-600 dark:text-woot-400 px-2 py-0.5 rounded border border-woot-100 dark:border-woot-800"
              >{{ v }}</span>
            </div>
          </div>

        </div>

      </div>

      <!-- ===== TAB: LEADS ===== -->
      <div v-if="activeTab === 'Leads'" class="flex-1 flex flex-col min-h-0">

        <div class="flex items-center gap-3 px-4 py-3 border-b border-slate-100 dark:border-slate-800 flex-shrink-0">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Buscar por número ou nome..."
            class="flex-1 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-1.5 text-sm bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-woot-300"
          />
          <select
            v-model="filterStatus"
            class="border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-1.5 text-sm bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 focus:outline-none"
          >
            <option value="">Todos</option>
            <option value="pending">Pendente</option>
            <option value="sent">Enviado</option>
            <option value="delivered">Recebido</option>
            <option value="read">Lido</option>
            <option value="replied">Respondeu</option>
            <option value="failed">Falhou</option>
          </select>
        </div>

        <div v-if="loadingContacts" class="flex items-center justify-center py-12 text-slate-400 text-sm gap-2 flex-1">
          Carregando leads...
        </div>

        <div v-else-if="filteredContacts.length === 0" class="flex flex-col items-center justify-center py-12 gap-2 text-slate-400 flex-1">
          <span class="text-3xl">👥</span>
          <p class="text-sm">{{ contacts.length === 0 ? 'Nenhum contato importado' : 'Nenhum resultado' }}</p>
        </div>

        <div v-else class="flex-1 overflow-y-auto">
          <table class="w-full text-sm">
            <thead class="bg-slate-50 dark:bg-slate-800 sticky top-0 z-10">
              <tr>
                <th class="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wide w-8">#</th>
                <th class="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">Número</th>
                <th class="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">Nome</th>
                <th class="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">Status</th>
                <th class="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">Canal</th>
                <th class="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">Enviado</th>
                <th class="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">Erro</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-50 dark:divide-slate-800/80">
              <tr
                v-for="(c, idx) in filteredContacts"
                :key="c.id"
                class="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors"
              >
                <td class="px-4 py-2.5 text-xs text-slate-300 dark:text-slate-600">{{ (contactPage - 1) * 50 + idx + 1 }}</td>
                <td class="px-4 py-2.5 font-mono text-sm text-slate-700 dark:text-slate-300">{{ formatPhone(c.phone_number) }}</td>
                <td class="px-4 py-2.5 text-slate-600 dark:text-slate-400">
                  {{ contactDisplayName(c) }}
                </td>
                <td class="px-4 py-2.5">
                  <span :class="contactStatusClass(c.status)" class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium">
                    <span :class="dotBgClass(c.status)" class="w-1.5 h-1.5 rounded-full flex-shrink-0"></span>
                    {{ contactStatusLabel(c.status) }}
                  </span>
                </td>
                <td class="px-4 py-2.5 text-xs text-slate-400 max-w-[120px] truncate" :title="c.sent_from_inbox_name || ''">
                  {{ c.sent_from_inbox_name || '—' }}
                </td>
                <td class="px-4 py-2.5 text-xs text-slate-400 whitespace-nowrap">{{ c.sent_at ? timeAgo(c.sent_at) : '—' }}</td>
                <td class="px-4 py-2.5 text-xs text-red-400 max-w-xs truncate" :title="c.error_message || ''">
                  {{ c.error_message || '—' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="totalContacts > 50" class="flex items-center justify-between px-4 py-3 border-t border-slate-100 dark:border-slate-800 flex-shrink-0">
          <span class="text-xs text-slate-500">
            {{ ((contactPage - 1) * 50) + 1 }}–{{ Math.min(contactPage * 50, totalContacts) }} de {{ totalContacts }}
          </span>
          <div class="flex gap-2">
            <button
              class="px-3 py-1 text-xs border border-slate-200 dark:border-slate-700 rounded-lg disabled:opacity-40 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              :disabled="contactPage <= 1"
              @click="loadContacts(contactPage - 1)"
            >← Anterior</button>
            <button
              class="px-3 py-1 text-xs border border-slate-200 dark:border-slate-700 rounded-lg disabled:opacity-40 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              :disabled="contactPage * 50 >= totalContacts"
              @click="loadContacts(contactPage + 1)"
            >Próximo →</button>
          </div>
        </div>

      </div>

      <!-- ===== TAB: IMPORTAR ===== -->
      <div v-if="activeTab === 'Importar'" class="flex-1 overflow-y-auto p-6 space-y-5">

        <div>
          <h4 class="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Importar / substituir contatos</h4>
          <p class="text-xs text-slate-400 mb-4">Importar um novo CSV substituirá os contatos pendentes. Contatos já enviados são mantidos.</p>

          <div
            class="border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors"
            :class="csvFile ? 'border-woot-400 bg-woot-50 dark:bg-woot-900/20' : 'border-slate-200 dark:border-slate-700 hover:border-woot-300 dark:hover:border-woot-600'"
            @click="$refs.csvInput.click()"
            @dragover.prevent
            @drop.prevent="onDrop"
          >
            <input ref="csvInput" type="file" accept=".csv,.txt" class="hidden" @change="onFileChange" />
            <div v-if="!csvFile">
              <p class="text-4xl mb-3">📂</p>
              <p class="text-sm text-slate-500">Arraste o CSV aqui ou <span class="text-woot-600 font-medium">clique para selecionar</span></p>
              <p class="text-xs text-slate-400 mt-1">Coluna obrigatória: <code class="bg-slate-100 dark:bg-slate-800 px-1 rounded">telefone</code> — Opcional: <code class="bg-slate-100 dark:bg-slate-800 px-1 rounded">nome</code></p>
              <p class="text-xs text-slate-400 mt-0.5">Todas as colunas disponíveis como <code class="bg-slate-100 dark:bg-slate-800 px-1 rounded">{{1}}</code>, <code class="bg-slate-100 dark:bg-slate-800 px-1 rounded">{{2}}</code>, <code class="bg-slate-100 dark:bg-slate-800 px-1 rounded">{{3}}</code>... na mensagem</p>
            </div>
            <div v-else class="text-sm">
              <p class="text-3xl mb-2">✅</p>
              <p class="font-medium text-slate-700 dark:text-slate-200 text-base">{{ csvFile.name }}</p>
              <p class="text-slate-400 text-sm mt-1">{{ csvPreviewCount != null ? csvPreviewCount + ' contatos detectados' : 'Arquivo selecionado' }}</p>
              <button class="text-xs text-red-400 hover:text-red-600 mt-3" @click.stop="csvFile = null; csvPreviewCount = null">Remover arquivo</button>
            </div>
          </div>

          <button
            v-if="csvFile"
            class="mt-4 w-full py-2.5 text-sm font-medium bg-woot-500 text-white rounded-lg hover:bg-woot-600 transition-colors disabled:opacity-50"
            :disabled="uploading"
            @click="uploadContacts"
          >
            {{ uploading ? 'Importando...' : `Importar ${csvPreviewCount != null ? csvPreviewCount + ' contatos' : ''}` }}
          </button>

          <div v-if="uploadResult" class="mt-3 p-3 bg-woot-50 dark:bg-woot-900/20 rounded-lg text-sm text-woot-700 dark:text-woot-400 flex items-center gap-2">
            <span>✅</span> {{ uploadResult }}
          </div>
          <div v-if="uploadError" class="mt-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg text-sm text-red-600 dark:text-red-400 flex items-center gap-2">
            <span>⚠</span> {{ uploadError }}
          </div>
        </div>

      </div>

    </div>
    <!-- Sprint #47.23: modal de confirmacao de cancelamento (TERMINAL) -->
    <div v-if="showCancelModal" class="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" @click.self="showCancelModal = false">
      <div class="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-md w-full p-6">
        <h3 class="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-4">⚠️ Cancelar campanha?</h3>
        <p class="text-sm text-slate-600 dark:text-slate-300 mb-1">
          Esta ação é <strong class="text-red-600">irreversível</strong>. A campanha será marcada como cancelada.
        </p>
        <p class="text-sm text-slate-600 dark:text-slate-300 mb-5">
          <strong>{{ stats.pending || 0 }} contato{{ (stats.pending || 0) === 1 ? '' : 's' }} ainda na fila</strong> não ser{{ (stats.pending || 0) === 1 ? 'á' : 'ão' }} enviado{{ (stats.pending || 0) === 1 ? '' : 's' }}.
        </p>
        <div class="flex gap-2 justify-end">
          <button class="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg" @click="showCancelModal = false">Voltar</button>
          <button class="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50" :disabled="actionLoading === 'cancel'" @click="cancelCampaign">{{ actionLoading === 'cancel' ? 'Cancelando...' : 'Sim, cancelar' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { BmtCampaignsApi } from '../../../../../api/bmtCampaigns';

const DOT_BG = {
  pending:   'bg-slate-300',
  sent:      'bg-blue-400',
  delivered: 'bg-indigo-500',
  read:      'bg-woot-400',
  replied:   'bg-emerald-500',
  failed:    'bg-red-400',
};

const STATUS_BADGE = {
  pending:   'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
  sent:      'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  delivered: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
  read:      'bg-woot-50 text-woot-700 dark:bg-woot-900/30 dark:text-woot-400',
  replied:   'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  failed:    'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

const STATUS_LABELS = {
  pending:   'Pendente',
  sent:      'Enviado',
  delivered: 'Recebido',
  read:      'Lido',
  replied:   'Respondeu',
  failed:    'Falhou',
};

// Ordem e cor dos segmentos da barra de progresso
const PROGRESS_SEGMENTS = [
  { key: 'replied',   label: 'Respondidas', bg: 'bg-emerald-500' },
  { key: 'read',      label: 'Lidos',       bg: 'bg-woot-400' },
  { key: 'delivered', label: 'Recebidos',   bg: 'bg-indigo-500' },
  { key: 'sent',      label: 'Enviados',    bg: 'bg-blue-400' },
  { key: 'failed',    label: 'Falhas',      bg: 'bg-red-400' },
  { key: 'pending',   label: 'Pendentes',   bg: 'bg-slate-200 dark:bg-slate-700' },
];

export default {
  name: 'BmtQrCampaignDetail',
  props: {
    campaign: { type: Object, required: true },
  },
  emits: ['close', 'refresh'],

  setup(props, { emit }) {
    const activeTab       = ref('Visão Geral');
    const contacts        = ref([]);
    const totalContacts   = ref(0);
    const contactPage     = ref(1);
    const loadingContacts = ref(false);
    const actionLoading   = ref(null);
    const csvFile         = ref(null);
    const csvPreviewCount = ref(null);
    const uploading       = ref(false);
    const uploadResult    = ref('');
    const uploadError     = ref('');
    const searchQuery     = ref('');
    const filterStatus    = ref('');
    const exporting       = ref(false);

    const stats = computed(() => props.campaign.stats || {
      total: 0, pending: 0, sent: 0, delivered: 0, read: 0, replied: 0, failed: 0,
    });

    const rules = computed(() => props.campaign.trigger_rules || {});

    const sentCount = computed(() =>
      (stats.value.sent || 0) + (stats.value.delivered || 0) + (stats.value.read || 0) + (stats.value.replied || 0)
    );

    const pct = (n) => stats.value.total > 0 ? Math.round((n / stats.value.total) * 100) : 0;

    const sentPct      = computed(() => pct(sentCount.value));
    // Sprint #47.24: processados = todos exceto pendentes (failed conta como processado, nao como enviado)
    const processedCount = computed(() => (stats.value.total || 0) - (stats.value.pending || 0));
    const processedPct   = computed(() => pct(processedCount.value));
    const deliveredPct = computed(() => sentCount.value > 0 ? Math.round(((stats.value.delivered || 0) / sentCount.value) * 100) : 0);
    const readPct      = computed(() => sentCount.value > 0 ? Math.round(((stats.value.read || 0) / sentCount.value) * 100) : 0);
    const failedPct    = computed(() => sentCount.value > 0 ? Math.round(((stats.value.failed || 0) / sentCount.value) * 100) : 0);
    const repliedPct   = computed(() => sentCount.value > 0 ? Math.round(((stats.value.replied || 0) / sentCount.value) * 100) : 0);

    const progressSegments = computed(() => {
      const total = stats.value.total || 0;
      return PROGRESS_SEGMENTS.map(s => ({
        ...s,
        count: stats.value[s.key] || 0,
        pct:   total > 0 ? Math.round(((stats.value[s.key] || 0) / total) * 100) : 0,
      })).filter(s => s.count > 0 || s.key === 'pending');
    });

    const messageVars = computed(() => {
      const msg = props.campaign.message || '';
      const found = [];
      const re = /\{\{(\w+|\d+)\}\}/g;
      let m;
      while ((m = re.exec(msg)) !== null) {
        if (!found.includes(m[0])) found.push(m[0]);
      }
      return found;
    });

    const canTrigger = computed(() => !props.campaign.paused && (stats.value.pending || 0) > 0);
    const canPause   = computed(() => !props.campaign.paused && (stats.value.pending || 0) > 0);

    const durationEstimate = computed(() => {
      const total = stats.value.total;
      if (!total) return null;
      const interval = rules.value.send_interval  || 20;
      const bSize    = rules.value.batch_size      || 0;
      const bPause   = rules.value.batch_interval  || 0;
      let sec = 0;
      if (bSize > 0 && bPause > 0) {
        const batches = Math.ceil(total / bSize);
        sec = ((batches - 1) * bPause) + ((total - 1) * interval);
      } else {
        sec = (total - 1) * interval;
      }
      if (sec < 60)   return `~${sec}s`;
      if (sec < 3600) return `~${Math.round(sec / 60)} min`;
      const h = Math.floor(sec / 3600);
      const mRem = Math.round((sec % 3600) / 60);
      return `~${h}h${mRem > 0 ? ` ${mRem}min` : ''}`;
    });

    const campaignStatusLabel = computed(() => {
      if (rules.value.cancelled) return 'Cancelada';
      if (props.campaign.paused) return 'Pausada';
      if (props.campaign.status === 'completed') return 'Concluída';
      if (props.campaign.status === 'active' && stats.value.total > 0) return 'Em execução';
      return 'Ativa';
    });

    const campaignStatusClass = computed(() => {
      if (rules.value.cancelled) return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
      if (props.campaign.paused) return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
      if (props.campaign.status === 'completed') return 'bg-woot-100 text-woot-700 dark:bg-woot-900/30 dark:text-woot-400';
      if (props.campaign.status === 'active') return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
      return 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300';
    });

    const filteredContacts = computed(() => {
      let list = contacts.value;
      if (filterStatus.value) list = list.filter(c => c.status === filterStatus.value);
      if (searchQuery.value) {
        const q = searchQuery.value.toLowerCase();
        list = list.filter(c =>
          (c.phone_number || '').includes(q) ||
          (c.name || '').toLowerCase().includes(q)
        );
      }
      return list;
    });

    async function loadContacts(page = 1) {
      loadingContacts.value = true;
      contactPage.value = page;
      try {
        const { data } = await BmtCampaignsApi.getContacts(props.campaign.id, page);
        contacts.value      = data.contacts || [];
        totalContacts.value = data.total    || 0;
      } catch { contacts.value = []; }
      finally { loadingContacts.value = false; }
    }

    function switchTab(tab) {
      activeTab.value = tab;
      if ((tab === 'Leads') && contacts.value.length === 0) loadContacts(1);
    }

    function goToLeadsTab(status) {
      filterStatus.value = status;
      activeTab.value = 'Leads';
      loadContacts(1);
    }

    onMounted(() => loadContacts(1));

    function onFileChange(e) { const f = e.target.files[0]; if (f) setFile(f); }
    function onDrop(e)       { const f = e.dataTransfer.files[0]; if (f) setFile(f); }

    function setFile(file) {
      csvFile.value = file;
      const reader  = new FileReader();
      reader.onload = ev => {
        const lines = ev.target.result.split('\n').filter(l => l.trim()).length;
        csvPreviewCount.value = Math.max(0, lines - 1);
      };
      reader.readAsText(file);
    }

    async function uploadContacts() {
      if (!csvFile.value) return;
      uploading.value    = true;
      uploadResult.value = '';
      uploadError.value  = '';
      try {
        const { data } = await BmtCampaignsApi.uploadContacts(props.campaign.id, csvFile.value);
        uploadResult.value    = `${data.imported} contatos importados com sucesso.`;
        csvFile.value         = null;
        csvPreviewCount.value = null;
        emit('refresh');
        await loadContacts(1);
      } catch (err) {
        uploadError.value = err?.response?.data?.error || 'Erro ao importar contatos.';
      } finally {
        uploading.value = false;
      }
    }

    async function triggerCampaign() {
      actionLoading.value = 'trigger';
      try {
        await BmtCampaignsApi.trigger(props.campaign.id);
        emit('refresh');
      } catch (err) {
        alert(err?.response?.data?.error || 'Erro ao disparar campanha');
      } finally { actionLoading.value = null; }
    }

    async function pauseCampaign() {
      actionLoading.value = 'pause';
      try {
        await BmtCampaignsApi.pause(props.campaign.id);
        emit('refresh');
      } catch { alert('Erro ao pausar'); }
      finally { actionLoading.value = null; }
    }

    async function resumeCampaign() {
      actionLoading.value = 'resume';
      try {
        await BmtCampaignsApi.resume(props.campaign.id);
        emit('refresh');
      } catch { alert('Erro ao retomar'); }
      finally { actionLoading.value = null; }
    }

    const showCancelModal = ref(false);
    async function cancelCampaign() {
      actionLoading.value = 'cancel';
      try {
        await BmtCampaignsApi.cancel(props.campaign.id);
        showCancelModal.value = false;
        emit('refresh');
      } catch (err) { alert(err?.response?.data?.error || 'Erro ao cancelar'); }
      finally { actionLoading.value = null; }
    }

    async function deleteCampaign() {
      if (!confirm(`Excluir a campanha "${props.campaign.title}"?`)) return;
      actionLoading.value = 'delete';
      try {
        await BmtCampaignsApi.destroy(props.campaign.id);
        emit('refresh');
        emit('close');
      } catch { alert('Erro ao excluir campanha'); }
      finally { actionLoading.value = null; }
    }

    function formatSeconds(s) {
      if (!s) return '—';
      s = parseInt(s);
      if (s < 60)   return `${s}s`;
      if (s < 3600) return `${Math.round(s / 60)} min`;
      const h = Math.floor(s / 3600);
      const m = Math.round((s % 3600) / 60);
      return m > 0 ? `${h}h ${m}min` : `${h}h`;
    }

    const contactStatusLabel = s => STATUS_LABELS[s] || s;
    const contactStatusClass  = s => STATUS_BADGE[s]  || 'bg-slate-100 text-slate-600';
    const dotBgClass          = s => DOT_BG[s]        || 'bg-slate-200';

    // Exibe nome do contato; se vazio, tenta buscar nos custom_fields (colunas do CSV)
    function contactDisplayName(c) {
      if (c.name && c.name.trim()) return c.name.trim();
      let cf = c.custom_fields || {};
      // custom_fields pode vir como string JSON — garantir objeto
      if (typeof cf === 'string') { try { cf = JSON.parse(cf); } catch { cf = {}; } }
      // Tenta colunas em ordem, pulando valores que são apenas números (telefone)
      for (let i = 1; i <= 10; i++) {
        const val = (cf[String(i)] || '').trim();
        if (val && !/^\+?[\d\s\-()+]+$/.test(val)) return val;
      }
      return '—';
    }

    function formatPhone(phone) {
      const d = (phone || '').replace(/\D/g, '');
      if (d.startsWith('55') && d.length === 13) return `+55 (${d.slice(2,4)}) ${d.slice(4,9)}-${d.slice(9)}`;
      if (d.startsWith('55') && d.length === 12) return `+55 (${d.slice(2,4)}) ${d.slice(4,8)}-${d.slice(8)}`;
      return phone || '';
    }

    function formatDate(iso) {
      if (!iso) return '—';
      return new Date(iso).toLocaleString('pt-BR', {
        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    }

    function timeAgo(iso) {
      if (!iso) return '—';
      const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
      if (diff < 60)    return `há ${diff}s`;
      if (diff < 3600)  return `há ${Math.floor(diff / 60)}min`;
      if (diff < 86400) return `há ${Math.floor(diff / 3600)}h`;
      return `há ${Math.floor(diff / 86400)}d`;
    }

    async function exportXlsx() {
      if (!props.campaign.id || exporting.value) return;
      exporting.value = true;
      try {
        const { data, headers } = await BmtCampaignsApi.exportContacts(props.campaign.id);
        const cd = headers['content-disposition'] || '';
        const match = cd.match(/filename="?([^";]+)"?/);
        const filename = match ? match[1] : `campanha_qr_${props.campaign.title || 'export'}.xlsx`;
        const blob = new Blob([data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (err) {
        alert(err?.response?.data?.error || 'Erro ao gerar planilha');
      } finally {
        exporting.value = false;
      }
    }

    return {
      activeTab, contacts, filteredContacts, totalContacts, contactPage,
      loadingContacts, actionLoading, stats, rules, sentCount,
      sentPct, deliveredPct, readPct, failedPct, repliedPct,
      processedCount, processedPct,
      progressSegments, messageVars,
      canTrigger, canPause, durationEstimate, campaignStatusLabel, campaignStatusClass,
      csvFile, csvPreviewCount, uploading, uploadResult, uploadError,
      searchQuery, filterStatus,
      exporting, exportXlsx,
      switchTab, goToLeadsTab, loadContacts, onFileChange, onDrop, uploadContacts,
      triggerCampaign, pauseCampaign, resumeCampaign, cancelCampaign, deleteCampaign,
      showCancelModal,
      contactStatusLabel, contactStatusClass, dotBgClass, contactDisplayName,
      formatPhone, formatDate, timeAgo, formatSeconds,
    };
  },
};
</script>
